// RTL (Right-to-Left) 工具函数
// 用于处理阿拉伯语等从右到左语言的布局和样式

/**
 * 检查当前语言是否为RTL语言
 * @returns {boolean}
 */
export function isRTLLanguage() {
  const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'yi'];
  const currentLang = localStorage.getItem('language') || 'en';
  return rtlLanguages.includes(currentLang);
}

/**
 * 获取当前语言的方向
 * @returns {string} 'rtl' 或 'ltr'
 */
export function getLanguageDirection() {
  return isRTLLanguage() ? 'rtl' : 'ltr';
}

/**
 * 设置HTML根元素的方向属性
 */
export function setDocumentDirection() {
  const direction = getLanguageDirection();
  document.documentElement.setAttribute('dir', direction);
  document.documentElement.setAttribute('lang', localStorage.getItem('language') || 'en');
}

/**
 * 获取RTL相关的CSS类名
 * @param {string} baseClass - 基础类名
 * @returns {string} 带RTL前缀的类名
 */
export function getRTLClass(baseClass) {
  return isRTLLanguage() ? `rtl-${baseClass}` : baseClass;
}

/**
 * 获取镜像后的边距值
 * @param {string} margin - 原始边距值，如 'ml-4', 'mr-2'
 * @returns {string} 镜像后的边距值
 */
export function getMirroredMargin(margin) {
  if (!isRTLLanguage()) return margin;
  
  const marginMap = {
    'ml-0': 'mr-0', 'mr-0': 'ml-0',
    'ml-1': 'mr-1', 'mr-1': 'ml-1',
    'ml-2': 'mr-2', 'mr-2': 'ml-2',
    'ml-3': 'mr-3', 'mr-3': 'ml-3',
    'ml-4': 'mr-4', 'mr-4': 'ml-4',
    'ml-auto': 'mr-auto', 'mr-auto': 'ml-auto',
  };
  
  return marginMap[margin] || margin;
}

/**
 * 获取镜像后的内边距值
 * @param {string} padding - 原始内边距值，如 'pl-4', 'pr-2'
 * @returns {string} 镜像后的内边距值
 */
export function getMirroredPadding(padding) {
  if (!isRTLLanguage()) return padding;
  
  const paddingMap = {
    'pl-0': 'pr-0', 'pr-0': 'pl-0',
    'pl-1': 'pr-1', 'pr-1': 'pl-1',
    'pl-2': 'pr-2', 'pr-2': 'pl-2',
    'pl-3': 'pr-3', 'pr-3': 'pl-3',
    'pl-4': 'pr-4', 'pr-4': 'pl-4',
  };
  
  return paddingMap[padding] || padding;
}

/**
 * 获取镜像后的定位值
 * @param {string} position - 原始定位值，如 'left-0', 'right-2'
 * @returns {string} 镜像后的定位值
 */
export function getMirroredPosition(position) {
  if (!isRTLLanguage()) return position;
  
  const positionMap = {
    'left-0': 'right-0', 'right-0': 'left-0',
    'left-1': 'right-1', 'right-1': 'left-1',
    'left-2': 'right-2', 'right-2': 'left-2',
    'left-4': 'right-4', 'right-4': 'left-4',
    'left-auto': 'right-auto', 'right-auto': 'left-auto',
  };
  
  return positionMap[position] || position;
}

/**
 * 获取镜像后的文本对齐方式
 * @param {string} align - 原始对齐方式，如 'text-left', 'text-right'
 * @returns {string} 镜像后的对齐方式
 */
export function getMirroredTextAlign(align) {
  if (!isRTLLanguage()) return align;
  
  const alignMap = {
    'text-left': 'text-right',
    'text-right': 'text-left',
  };
  
  return alignMap[align] || align;
}

/**
 * 获取镜像后的Flex方向
 * @param {string} direction - 原始Flex方向，如 'flex-row', 'flex-row-reverse'
 * @returns {string} 镜像后的Flex方向
 */
export function getMirroredFlexDirection(direction) {
  if (!isRTLLanguage()) return direction;
  
  const directionMap = {
    'flex-row': 'flex-row-reverse',
    'flex-row-reverse': 'flex-row',
  };
  
  return directionMap[direction] || direction;
}

/**
 * 获取镜像后的边框值
 * @param {string} border - 原始边框值，如 'border-l', 'border-r'
 * @returns {string} 镜像后的边框值
 */
export function getMirroredBorder(border) {
  if (!isRTLLanguage()) return border;
  
  const borderMap = {
    'border-l': 'border-r',
    'border-r': 'border-l',
    'border-l-2': 'border-r-2',
    'border-r-2': 'border-l-2',
  };
  
  return borderMap[border] || border;
}

/**
 * 获取镜像后的圆角值
 * @param {string} rounded - 原始圆角值，如 'rounded-l', 'rounded-r'
 * @returns {string} 镜像后的圆角值
 */
export function getMirroredRounded(rounded) {
  if (!isRTLLanguage()) return rounded;
  
  const roundedMap = {
    'rounded-l': 'rounded-r',
    'rounded-r': 'rounded-l',
    'rounded-l-md': 'rounded-r-md',
    'rounded-r-md': 'rounded-l-md',
  };
  
  return roundedMap[rounded] || rounded;
}

/**
 * 获取镜像后的图标名称
 * @param {string} iconName - 原始图标名称，如 'arrow-left', 'arrow-right'
 * @returns {string} 镜像后的图标名称
 */
export function getMirroredIcon(iconName) {
  if (!isRTLLanguage()) return iconName;
  
  const iconMap = {
    'arrow-left': 'arrow-right',
    'arrow-right': 'arrow-left',
    'chevron-left': 'chevron-right',
    'chevron-right': 'chevron-left',
    'caret-left': 'caret-right',
    'caret-right': 'caret-left',
    'angle-left': 'angle-right',
    'angle-right': 'angle-left',
  };
  
  return iconMap[iconName] || iconName;
}

/**
 * 获取镜像后的动画名称
 * @param {string} animation - 原始动画名称，如 'slideInLeft', 'slideInRight'
 * @returns {string} 镜像后的动画名称
 */
export function getMirroredAnimation(animation) {
  if (!isRTLLanguage()) return animation;
  
  const animationMap = {
    'slideInLeft': 'slideInRight',
    'slideInRight': 'slideInLeft',
    'slideOutLeft': 'slideOutRight',
    'slideOutRight': 'slideOutLeft',
    'fadeInLeft': 'fadeInRight',
    'fadeInRight': 'fadeInLeft',
    'fadeOutLeft': 'fadeOutRight',
    'fadeOutRight': 'fadeOutLeft',
  };
  
  return animationMap[animation] || animation;
}

/**
 * 获取RTL相关的样式对象
 * @param {Object} styles - 原始样式对象
 * @returns {Object} 镜像后的样式对象
 */
export function getRTLStyles(styles) {
  if (!isRTLLanguage()) return styles;
  
  const rtlStyles = { ...styles };
  
  // 镜像边距
  if (rtlStyles.marginLeft) {
    rtlStyles.marginRight = rtlStyles.marginLeft;
    delete rtlStyles.marginLeft;
  }
  if (rtlStyles.marginRight) {
    rtlStyles.marginLeft = rtlStyles.marginRight;
    delete rtlStyles.marginRight;
  }
  
  // 镜像内边距
  if (rtlStyles.paddingLeft) {
    rtlStyles.paddingRight = rtlStyles.paddingLeft;
    delete rtlStyles.paddingLeft;
  }
  if (rtlStyles.paddingRight) {
    rtlStyles.paddingLeft = rtlStyles.paddingRight;
    delete rtlStyles.paddingRight;
  }
  
  // 镜像定位
  if (rtlStyles.left !== undefined) {
    rtlStyles.right = rtlStyles.left;
    delete rtlStyles.left;
  }
  if (rtlStyles.right !== undefined) {
    rtlStyles.left = rtlStyles.right;
    delete rtlStyles.right;
  }
  
  // 镜像文本对齐
  if (rtlStyles.textAlign === 'left') {
    rtlStyles.textAlign = 'right';
  } else if (rtlStyles.textAlign === 'right') {
    rtlStyles.textAlign = 'left';
  }
  
  // 镜像Flex方向
  if (rtlStyles.flexDirection === 'row') {
    rtlStyles.flexDirection = 'row-reverse';
  } else if (rtlStyles.flexDirection === 'row-reverse') {
    rtlStyles.flexDirection = 'row';
  }
  
  return rtlStyles;
}

/**
 * 初始化RTL支持
 * 在应用启动时调用
 */
export function initRTLSupport() {
  setDocumentDirection();
  
  // 监听语言变化
  window.addEventListener('storage', (e) => {
    if (e.key === 'language') {
      setDocumentDirection();
    }
  });
}

/**
 * 获取RTL相关的Vue计算属性
 * @returns {Object} Vue计算属性对象
 */
export function getRTLComputed() {
  return {
    isRTL: isRTLLanguage,
    direction: getLanguageDirection,
    rtlClass: (baseClass) => getRTLClass(baseClass),
  };
}


